import { useState } from "react";
import heroImg from "../../images/Section 09.webp";
import styles from "./Section09CTA.module.scss";
import ThankYouModal from "../common/ThankYouModal";

const CheckCircleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#071b52" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

const GearIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#071b52" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="3"></circle>
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
  </svg>
);

const UserIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#071b52" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
    <circle cx="12" cy="7" r="4"></circle>
  </svg>
);

const ScissorIcon = () => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    width="20" 
    height="20" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={styles.scissorIcon}
  >
    <g className={styles.bladeTop}>
      <circle cx="6" cy="6" r="3" />
      <line x1="8.12" y1="8.12" x2="20" y2="20" />
    </g>
    <g className={styles.bladeBottom}>
      <circle cx="6" cy="18" r="3" />
      <line x1="8.12" y1="15.88" x2="20" y2="4" />
    </g>
  </svg>
);

const Section09CTA = () => {
  const [isThankYouModalOpen, setIsThankYouModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    salonName: "",
    city: "",
    salonType: "Unisex Salon",
    operatingChairs: ""
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Apply proper rules based on field type
    if (["fullName", "salonName", "city"].includes(name)) {
      // Allow only letters and spaces (characters)
      if (!/^[a-zA-Z\s]*$/.test(value)) return;
    }

    if (["phoneNumber", "operatingChairs"].includes(name)) {
      // Allow only numeric digits
      if (!/^\d*$/.test(value)) return;
    }

    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";

    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^\d{10,15}$/.test(formData.phoneNumber.replace(/[\s-]/g, ''))) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    if (!formData.salonName.trim()) newErrors.salonName = "Salon name is required";
    if (!formData.city.trim()) newErrors.city = "City is required";

    if (!formData.operatingChairs.trim()) {
      newErrors.operatingChairs = "Number of chairs is required";
    } else if (isNaN(formData.operatingChairs) || parseInt(formData.operatingChairs) <= 0) {
      newErrors.operatingChairs = "Please enter a valid number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault(); // Added this in case form is submitted via enter key
    if (validateForm()) {
      setIsThankYouModalOpen(true);
      // Optional: clear form or handle API call here
    }
  };

  return (
    <section id="request-review" className={`section ${styles.section9}`}>
      <div className={`container ${styles.container}`}>
        <div className={styles.section9Content}>
          <div className={styles.topRow}>
            <div className={styles.leftContent}>
              {/* <span className={styles.badge}>SECTION 09</span> */}
              <h2 className={styles.heading}>
                <span>Take the next step toward</span>
                <span className={styles.headingRow}>
                  <span className={styles.highlight}>clearer weekly salon</span> decisions
                </span>
              </h2>
              <p className={styles.description}>
                If you want to understand what your salon’s numbers may be hiding, request your review and begin with one simple next step toward clearer weekly visibility.
              </p>

              <div className={styles.features}>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon} aria-hidden="true"><CheckCircleIcon /></div>
                  <span>Simple process</span>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon} aria-hidden="true"><GearIcon /></div>
                  <span>No complicated setup</span>
                </div>
                <div className={styles.featureCard}>
                  <div className={styles.featureIcon} aria-hidden="true"><UserIcon /></div>
                  <span>No accounting knowledge required</span>
                </div>
              </div>

              <div className={styles.featureNoteWrapper}>
                <p className={styles.featureNote}>
                  Share a few basic details below, and we will take the next step with you from there.
                </p>
              </div>

              <div className={styles.bottomBanner}>
                <div className={styles.bannerLeft}>
                  <span className={styles.bannerIcon}>🛡️</span>
                  <p>
                    Built to stay <span>simple, practical, and trustworthy</span>.
                  </p>
                </div>
                <div className={styles.bannerDivider} />
                <div className={styles.bannerRight}>
                  <span className={styles.bannerIcon}>🔒</span>
                  <p>
                    Your data is <span>private, secure, and never shared</span>.
                  </p>
                </div>
              </div>
            </div>

            <div className={styles.rightColumn}>
              <div className={styles.heroImageWrapper}>
                <img
                  src={heroImg}
                  alt="Salon owner working with dashboard"
                  className={styles.heroImage}
                 loading="lazy" decoding="async"/>
              </div>

              <div className={styles.formCard}>
                <div className={styles.formTitleRow}>
                  <div className={styles.formTitleDots} />
                  <h3 className={styles.formTitle}>Request My Review</h3>
                  <div className={styles.formTitleDots} />
                </div>
                <form className={styles.formGrid} onSubmit={handleSubmit} noValidate>
                  <label className={styles.field}>
                    <span className={styles.label}>Full Name</span>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Your name"
                      className={`${styles.input} ${errors.fullName ? styles.inputError : ''}`}
                      value={formData.fullName}
                      onChange={handleChange}
                    />
                    {errors.fullName && <span className={styles.errorText}>{errors.fullName}</span>}
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Phone Number</span>
                    <input
                      type="tel"
                      name="phoneNumber"
                      placeholder="Your phone"
                      className={`${styles.input} ${errors.phoneNumber ? styles.inputError : ''}`}
                      value={formData.phoneNumber}
                      onChange={handleChange}
                    />
                    {errors.phoneNumber && <span className={styles.errorText}>{errors.phoneNumber}</span>}
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Salon Name</span>
                    <input
                      type="text"
                      name="salonName"
                      placeholder="Salon name"
                      className={`${styles.input} ${errors.salonName ? styles.inputError : ''}`}
                      value={formData.salonName}
                      onChange={handleChange}
                    />
                    {errors.salonName && <span className={styles.errorText}>{errors.salonName}</span>}
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>City</span>
                    <input
                      type="text"
                      name="city"
                      placeholder="City"
                      className={`${styles.input} ${errors.city ? styles.inputError : ''}`}
                      value={formData.city}
                      onChange={handleChange}
                    />
                    {errors.city && <span className={styles.errorText}>{errors.city}</span>}
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Type of Salon</span>
                    <select
                      name="salonType"
                      className={`${styles.select} ${errors.salonType ? styles.selectError : ''}`}
                      value={formData.salonType}
                      onChange={handleChange}
                    >
                      <option value="Unisex Salon">Unisex Salon</option>
                      <option value="Men's Salon">Men's Salon</option>
                      <option value="Beauty Parlour">Beauty Parlour</option>
                    </select>
                    {errors.salonType && <span className={styles.errorText}>{errors.salonType}</span>}
                  </label>
                  <label className={styles.field}>
                    <span className={styles.label}>Number of Operating Chairs</span>
                    <input
                      type="text"
                      name="operatingChairs"
                      placeholder="e.g. 8"
                      className={`${styles.input} ${errors.operatingChairs ? styles.inputError : ''}`}
                      value={formData.operatingChairs}
                      onChange={handleChange}
                    />
                    {errors.operatingChairs && <span className={styles.errorText}>{errors.operatingChairs}</span>}
                  </label>
                </form>
                <button
                  type="button"
                  className={styles.submitButton}
                  onClick={handleSubmit}
                >
                  Request My Review
                  <ScissorIcon />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ThankYouModal
        isOpen={isThankYouModalOpen}
        onClose={() => setIsThankYouModalOpen(false)}
      />
    </section>
  );
};

export default Section09CTA;
